import { useLayoutEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import JsBarcode from "jsbarcode";
import { ImageIcon } from "lucide-react";
import { PT_TO_MM, fillTemplate } from "@/lib/card";
import { assetUrl } from "@/lib/api";

const BarcodeView = ({ el, scale, data }) => {
  const ref = useRef(null);
  const [err, setErr] = useState(false);
  const value = fillTemplate(el.value, data);
  useLayoutEffect(() => {
    const svg = ref.current;
    try {
      JsBarcode(svg, value || " ", { format: el.format || "CODE128", displayValue: false, margin: 0, lineColor: el.color, background: "transparent", height: 100, width: 2 });
      const w = parseFloat(svg.getAttribute("width")), h = parseFloat(svg.getAttribute("height"));
      svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
      svg.removeAttribute("width");
      svg.removeAttribute("height");
      svg.setAttribute("preserveAspectRatio", "none");
      setErr(false);
    } catch {
      setErr(true);
    }
  }, [value, el.color, el.format]);
  const fs = (el.fontSize || 5) * PT_TO_MM * scale;
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", background: el.bg }}>
      <svg ref={ref} style={{ flex: 1, width: "100%", minHeight: 0, display: err ? "none" : "block" }} />
      {err && <div style={{ flex: 1, fontSize: fs, color: "#DC2626" }} className="grid place-items-center text-center">Invalid {el.format}</div>}
      {el.showText && (
        <div style={{ fontSize: fs, lineHeight: 1.1, color: el.color, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", letterSpacing: fs * 0.15, paddingTop: fs * 0.15 }}>
          {value}
        </div>
      )}
    </div>
  );
};

const TextView = ({ el, scale, data }) => (
  <div
    style={{
      width: "100%", height: "100%", overflow: "hidden", whiteSpace: "pre-wrap", wordBreak: "break-word",
      fontFamily: `'${el.fontFamily}', sans-serif`, fontSize: el.fontSize * PT_TO_MM * scale,
      color: el.color, fontWeight: el.fontWeight, fontStyle: el.italic ? "italic" : "normal",
      textAlign: el.align, lineHeight: el.lineHeight || 1.15,
      letterSpacing: (el.letterSpacing || 0) * PT_TO_MM * scale,
      textTransform: el.uppercase ? "uppercase" : "none",
    }}
  >
    {fillTemplate(el.text, data)}
  </div>
);

const ShapeView = ({ el, scale }) => (
  <div
    style={{
      width: "100%", height: "100%", boxSizing: "border-box", background: el.fill,
      border: el.strokeWidth ? `${el.strokeWidth * scale}px solid ${el.stroke}` : "none",
      borderRadius: el.type === "ellipse" ? "50%" : (el.radius || 0) * scale,
    }}
  />
);

const ImageView = ({ el, scale, data, editing }) => {
  const src = (el.field && data && data[el.field]) || el.src;
  const box = {
    width: "100%", height: "100%", boxSizing: "border-box", overflow: "hidden",
    borderRadius: (el.radius || 0) * scale,
    border: el.strokeWidth ? `${el.strokeWidth * scale}px solid ${el.stroke}` : "none",
  };
  if (src) {
    return (
      <div style={box}>
        <img src={assetUrl(src)} alt="" crossOrigin="anonymous" draggable={false}
          style={{ width: "100%", height: "100%", objectFit: el.fit || "cover", display: "block" }} />
      </div>
    );
  }
  if (!editing) return <div style={box} />;
  return (
    <div style={{ ...box, background: "repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 6px,#f1f5f9 6px,#f1f5f9 12px)" }}
      className="flex flex-col items-center justify-center text-slate-500">
      <ImageIcon size={Math.max(10, Math.min(el.w, el.h) * scale * 0.3)} />
      {el.field && <span style={{ fontSize: Math.max(8, scale * 1.6) }} className="font-mono">{el.field}</span>}
    </div>
  );
};

const QrView = ({ el, data }) => (
  <QRCodeSVG value={fillTemplate(el.value, data) || " "} fgColor={el.color} bgColor={el.bg}
    level="M" style={{ width: "100%", height: "100%", display: "block" }} />
);

export const ElementView = (props) => {
  const t = props.el.type;
  if (t === "text") return <TextView {...props} />;
  if (t === "image") return <ImageView {...props} />;
  if (t === "qr") return <QrView {...props} />;
  if (t === "barcode") return <BarcodeView {...props} />;
  return <ShapeView {...props} />;
};
